import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useProcessing, {
    ProcessingEnvironment,
    Processor,
} from '@/src/Processing/components/hooks/useProcessing';
import { Switch } from '@/src/shadComponents/ui/switch';
import useProcessingPresets from '@/src/Processing/components/hooks/useProcessingPresets';
import AssetPicker from '../components/AssetPicker';
import SearchField from '../components/SearchField';

/*
 * Flow editor v2 — the pipeline is the page: steps on the numbered
 * spine (same visual grammar as the Sharing journey), arguments
 * rendered by type (selects for enums, asset tiles for fileurl,
 * underline inputs otherwise), and a rail that replaces the classic
 * modals: flow settings + an always-visible searchable processor
 * catalog. Saves through the classic create/update endpoints.
 */

const HIDDEN_ARGS = new Set(['in', 'out']);

function stepTitle(s: Processor) {
    return s.title || s.name;
}

function fileName(url: string) {
    try {
        const clean = url.split('?')[0];
        return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
    } catch {
        return url;
    }
}

/* ------------------------------ editor ------------------------------ */

export default function FlowEditorV2() {
    const navigate = useNavigate();
    const { activationId, flowId } = useParams();
    const id = activationId ? +activationId : 0;
    const isNew = flowId == null;
    const { getGalleriesForActivation } = useGetGalleries();
    const {
        getProcessingFlow,
        processors: catalog,
        getProcessingEnvironments,
        createProcessingFlow,
        updateProcessingFlow,
        testFlow,
    } = useProcessing();
    const { presets, savePreset } = useProcessingPresets();

    const [galleryId, setGalleryId] = useState<number | null>(null);
    const [steps, setSteps] = useState<Processor[]>([]);
    const [environmentId, setEnvironmentId] = useState<number | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [envs, setEnvs] = useState<ProcessingEnvironment[]>([]);
    const [search, setSearch] = useState('');
    const [picker, setPicker] = useState<{
        step: number;
        arg: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);
    const snapshot = useRef('');
    const testFileRef = useRef<HTMLInputElement>(null);

    const runTest = async (file: File) => {
        setTestBusy(true);
        setTestResult(null);
        const result = await testFlow({
            // @ts-ignore FormData accepts File
            asset: file,
            flow: JSON.stringify(steps),
        }).catch(() => null);
        setTestResult(result ?? { error: 'Test failed' });
        setTestBusy(false);
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [galleries, envRows] = await Promise.all([
                    getGalleriesForActivation(id),
                    getProcessingEnvironments(),
                ]);
                if (!mounted) return;
                setGalleryId(galleries?.[0]?.id ?? null);
                setEnvs(envRows ?? []);
                let init = {
                    steps: [] as Processor[],
                    environmentId: envRows?.[0]?.id ?? null,
                    enabled: true,
                };
                if (!isNew) {
                    const flow = await getProcessingFlow(+flowId!);
                    if (!mounted) return;
                    if (flow) {
                        let parsed: Processor[] = [];
                        try {
                            const raw = JSON.parse(flow.flowDefinition);
                            if (Array.isArray(raw)) parsed = raw;
                        } catch {
                            /* treat as empty */
                        }
                        init = {
                            steps: parsed,
                            environmentId: flow.environmentId,
                            enabled: !!flow.enabled,
                        };
                    }
                }
                setSteps(init.steps);
                setEnvironmentId(init.environmentId);
                setEnabled(init.enabled);
                snapshot.current = JSON.stringify(init);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, flowId]);

    const dirty =
        !isLoading &&
        JSON.stringify({ steps, environmentId, enabled }) !==
            snapshot.current;

    const envName =
        envs.find((e) => e.id === environmentId)?.environment ?? '';

    const setArg = (si: number, key: string, value: string | number) => {
        setSteps((rows) =>
            rows.map((s, i) =>
                i === si
                    ? { ...s, arguments: { ...s.arguments, [key]: value } }
                    : s,
            ),
        );
    };

    const move = (si: number, dir: -1 | 1) => {
        setSteps((rows) => {
            const next = [...rows];
            const to = si + dir;
            if (to < 0 || to >= next.length) return rows;
            [next[si], next[to]] = [next[to], next[si]];
            return next;
        });
    };

    const addStep = (p: Processor) => {
        setSteps((rows) => [...rows, JSON.parse(JSON.stringify(p))]);
    };

    const save = async () => {
        if (isSaving || environmentId == null) return;
        setIsSaving(true);
        setSaveError(false);
        const def = JSON.stringify(steps);
        try {
            const ok = isNew
                ? galleryId != null &&
                  (await createProcessingFlow(
                      galleryId,
                      def,
                      environmentId,
                      enabled,
                  ))
                : await updateProcessingFlow(
                      +flowId!,
                      def,
                      environmentId,
                      enabled,
                  );
            if (ok) {
                navigate(`/v2/activations/${id}/processing`);
                return;
            }
            setSaveError(true);
        } catch {
            setSaveError(true);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredCatalog = useMemo(() => {
        const q = search.toLowerCase();
        return catalog.filter(
            (p) =>
                !q ||
                p.name.toLowerCase().includes(q) ||
                (p.title ?? '').toLowerCase().includes(q) ||
                (p.description ?? '').toLowerCase().includes(q),
        );
    }, [catalog, search]);

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() =>
                        navigate(`/v2/activations/${id}/processing`)
                    }
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Processing
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">
                    {isNew ? 'New flow' : 'Edit flow'}
                </span>
            </div>

            <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_300px]">
                {/* MAIN — the pipeline */}
                <div className="md:pr-[42px]">
                    <div className="mb-4 flex h-8 items-center justify-between">
                        <span className="ps-label">
                            Pipeline · runs top to bottom
                        </span>
                        <span className="flex items-center gap-3">
                            {saveError && (
                                <span className="text-[11.5px] font-semibold text-attention">
                                    Save failed — try again
                                </span>
                            )}
                            {(dirty || isNew) && (
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={isSaving}
                                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                                >
                                    {isSaving
                                        ? 'Saving…'
                                        : isNew
                                        ? 'Create flow'
                                        : 'Save changes'}
                                </button>
                            )}
                        </span>
                    </div>

                    {steps.length === 0 && (
                        <p className="mb-6 max-w-[46ch] text-[13px] text-muted-foreground">
                            An empty pipeline — captures pass through
                            untouched. Add a step from the catalog.
                        </p>
                    )}

                    {steps.map((s, si) => {
                        const unsupported =
                            s.environments &&
                            envName &&
                            !s.environments.includes(envName);
                        const argKeys = Object.keys(s.arguments ?? {}).filter(
                            (k) => !HIDDEN_ARGS.has(k),
                        );
                        return (
                            <div
                                key={si}
                                className="relative pb-[30px] pl-9 last:pb-2"
                            >
                                {si < steps.length && (
                                    <span className="absolute bottom-0 left-[10px] top-[26px] w-px bg-line" />
                                )}
                                <span className="absolute left-0 top-0.5 flex h-[21px] w-[21px] items-center justify-center rounded-full bg-brand font-mono text-[10px] text-brand-foreground">
                                    {si + 1}
                                </span>
                                <div className="flex items-start gap-2.5">
                                    <div className="min-w-0">
                                        <span className="text-[14.5px] font-semibold tracking-[-0.01em]">
                                            {stepTitle(s)}
                                            <span className="ml-2 font-mono text-[10.5px] font-normal text-faint">
                                                {s.name}
                                            </span>
                                        </span>
                                        {s.description && (
                                            <div className="mt-0.5 max-w-[56ch] truncate text-[12px] text-muted-foreground">
                                                {s.description}
                                            </div>
                                        )}
                                        {unsupported && (
                                            <div className="mt-1 text-[11.5px] font-semibold text-attention">
                                                Not supported in the {envName}{' '}
                                                environment
                                            </div>
                                        )}
                                    </div>
                                    <div className="ml-auto flex flex-none items-center gap-0.5">
                                        <button
                                            type="button"
                                            title="Move up"
                                            onClick={() => move(si, -1)}
                                            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent text-faint hover:bg-secondary hover:text-foreground"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            type="button"
                                            title="Move down"
                                            onClick={() => move(si, 1)}
                                            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent text-faint hover:bg-secondary hover:text-foreground"
                                        >
                                            ↓
                                        </button>
                                        <button
                                            type="button"
                                            title="Remove step"
                                            onClick={() =>
                                                setSteps((rows) =>
                                                    rows.filter(
                                                        (_, i) => i !== si,
                                                    ),
                                                )
                                            }
                                            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent text-faint hover:bg-secondary hover:text-attention"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-3 grid max-w-[560px] gap-x-[26px] gap-y-3.5 sm:grid-cols-2">
                                    {argKeys.map((key) => {
                                        const type = s.argumentTypes?.[key];
                                        const value = s.arguments[key];
                                        if (type === 'fileurl') {
                                            const v = String(value ?? '');
                                            return (
                                                <div key={key}>
                                                    <label className="mb-[5px] block text-[11.5px] font-semibold capitalize text-muted-foreground">
                                                        {key}
                                                    </label>
                                                    <div className="flex items-center gap-2.5">
                                                        <span className="h-[38px] w-[52px] flex-none overflow-hidden rounded-[8px] bg-secondary">
                                                            {v &&
                                                                /^https?:/.test(
                                                                    v,
                                                                ) && (
                                                                    <img
                                                                        src={v}
                                                                        alt=""
                                                                        className="h-full w-full object-contain"
                                                                    />
                                                                )}
                                                        </span>
                                                        <span className="min-w-0">
                                                            <span className="block overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px]">
                                                                {v
                                                                    ? fileName(
                                                                          v,
                                                                      )
                                                                    : '—'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setPicker({
                                                                        step: si,
                                                                        arg: key,
                                                                    })
                                                                }
                                                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-brand-ink"
                                                            >
                                                                {v
                                                                    ? 'Replace'
                                                                    : 'Choose'}
                                                            </button>
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        if (Array.isArray(type)) {
                                            const opts = type.includes(
                                                String(value),
                                            )
                                                ? type
                                                : [...type, String(value)];
                                            return (
                                                <div key={key}>
                                                    <label className="mb-[5px] block text-[11.5px] font-semibold capitalize text-muted-foreground">
                                                        {key}
                                                    </label>
                                                    <select
                                                        className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                                        value={String(value)}
                                                        onChange={(e) =>
                                                            setArg(
                                                                si,
                                                                key,
                                                                e.target
                                                                    .value,
                                                            )
                                                        }
                                                    >
                                                        {opts.map((o) => (
                                                            <option
                                                                key={o}
                                                                value={o}
                                                            >
                                                                {o}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={key}>
                                                <label className="mb-[5px] block text-[11.5px] font-semibold capitalize text-muted-foreground">
                                                    {key}
                                                </label>
                                                <input
                                                    className="ps-input w-full font-mono text-[12px]"
                                                    value={String(
                                                        value ?? '',
                                                    )}
                                                    onChange={(e) =>
                                                        setArg(
                                                            si,
                                                            key,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                    <div className="sm:col-span-2">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setSteps((rows) =>
                                                    rows.map((r, i) =>
                                                        i === si
                                                            ? {
                                                                  ...r,
                                                                  saveOutput:
                                                                      !r.saveOutput,
                                                              }
                                                            : r,
                                                    ),
                                                )
                                            }
                                            className={
                                                'flex cursor-pointer items-center gap-1.5 rounded-full px-[11px] py-1 font-[inherit] text-[11.5px] font-semibold ' +
                                                (s.saveOutput
                                                    ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                    : 'border border-border bg-transparent text-faint')
                                            }
                                        >
                                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                            {s.saveOutput
                                                ? 'Output saved to gallery'
                                                : 'Output not saved'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* add node */}
                    <div className="relative pb-2 pl-9">
                        <span className="absolute left-0 top-0 flex h-[21px] w-[21px] items-center justify-center rounded-full border-[1.5px] border-dashed border-off bg-background text-[12px] text-faint">
                            +
                        </span>
                        <div className="pt-0.5 text-[12.5px] text-faint">
                            Add a step — pick a processor from the{' '}
                            <b className="font-semibold text-muted-foreground">
                                catalog →
                            </b>
                        </div>
                    </div>
                </div>

                {/* RAIL */}
                <div className="mt-9 md:mt-0 md:border-l md:border-line md:pl-[42px]">
                    <div className="mb-[30px]">
                        <div className="ps-label mb-3">Flow settings</div>
                        <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                            {envs.map((e) => (
                                <button
                                    key={e.id}
                                    type="button"
                                    onClick={() => setEnvironmentId(e.id)}
                                    className={
                                        'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold capitalize ' +
                                        (environmentId === e.id
                                            ? 'bg-background text-foreground'
                                            : 'bg-transparent text-muted-foreground')
                                    }
                                >
                                    {e.environment}
                                </button>
                            ))}
                        </div>
                        <div className="mt-3.5 flex items-center justify-between gap-3">
                            <span className="text-[12.5px] font-semibold">
                                Enabled
                            </span>
                            <Switch
                                checked={enabled}
                                onCheckedChange={setEnabled}
                            />
                        </div>
                    </div>

                    <div className="mb-[30px]">
                        <div className="ps-label mb-3">Add step</div>
                        <SearchField
                            className="mb-1.5 w-full"
                            placeholder={`Search ${catalog.length} processors…`}
                            value={search}
                            onChange={setSearch}
                        />
                        <div className="max-h-[340px] overflow-y-auto">
                            {filteredCatalog.map((p, i) => (
                                <button
                                    key={p.name}
                                    type="button"
                                    onClick={() => addStep(p)}
                                    className={
                                        'group block w-full cursor-pointer border-0 bg-transparent px-0.5 py-[9px] text-left font-[inherit] ' +
                                        (i > 0 ? 'border-t border-line' : '')
                                    }
                                >
                                    <span className="block text-[12.5px] font-semibold group-hover:text-brand-ink">
                                        {stepTitle(p)}
                                        {p.environments &&
                                            p.environments.length > 0 && (
                                                <span className="ml-1.5 font-mono text-[9px] font-normal uppercase tracking-[0.08em] text-faint">
                                                    {p.environments.join(
                                                        ' · ',
                                                    )}
                                                </span>
                                            )}
                                    </span>
                                    {p.description && (
                                        <span className="mt-px block truncate text-[11px] text-faint">
                                            {p.description}
                                        </span>
                                    )}
                                </button>
                            ))}
                            {filteredCatalog.length === 0 && (
                                <p className="py-3 text-[12px] text-faint">
                                    No processors match.
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="mb-[30px]">
                        <div className="ps-label mb-2">Presets</div>
                        {presets.length === 0 && (
                            <p className="text-[12px] text-faint">
                                None saved yet.
                            </p>
                        )}
                        {presets.map((pr: any) => (
                            <button
                                key={pr.id}
                                type="button"
                                title={pr.description ?? ''}
                                onClick={() =>
                                    Array.isArray(pr.steps) &&
                                    setSteps((rows) => [
                                        ...rows,
                                        ...JSON.parse(
                                            JSON.stringify(pr.steps),
                                        ),
                                    ])
                                }
                                className="block w-full cursor-pointer border-0 bg-transparent px-0.5 py-1.5 text-left font-[inherit] text-[12.5px] font-semibold hover:text-brand-ink"
                            >
                                {pr.name}
                                <span className="ml-1.5 font-normal text-[10.5px] text-faint">
                                    {(pr.steps ?? []).length} steps
                                </span>
                            </button>
                        ))}
                        {steps.length > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    const name = window.prompt(
                                        'Preset name:',
                                    );
                                    if (name?.trim())
                                        savePreset(name.trim(), steps as any);
                                }}
                                className="mt-1.5 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                + Save current as preset
                            </button>
                        )}
                    </div>

                    {['cloud', 'text'].includes(envName) && (
                        <div className="mb-[30px]">
                            <div className="ps-label mb-2">Try it</div>
                            <button
                                type="button"
                                disabled={testBusy || steps.length === 0}
                                onClick={() => testFileRef.current?.click()}
                                className="w-full cursor-pointer rounded-[9px] border border-border bg-transparent px-3 py-2 text-left font-[inherit] text-[12.5px] font-semibold text-foreground hover:border-faint disabled:opacity-50"
                            >
                                {testBusy
                                    ? 'Running…'
                                    : 'Test flow with a sample photo'}
                            </button>
                            <input
                                ref={testFileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = '';
                                    if (f) runTest(f);
                                }}
                            />
                            {testResult && (
                                <div className="mt-2 text-[11.5px]">
                                    {(() => {
                                        const url =
                                            testResult.url ??
                                            testResult.uri ??
                                            testResult.outputUrl ??
                                            testResult.output;
                                        if (
                                            typeof url === 'string' &&
                                            /^https?:/.test(url)
                                        )
                                            return (
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="font-semibold text-brand-ink"
                                                >
                                                    <img
                                                        src={url}
                                                        alt="Test result"
                                                        className="mb-1 w-full rounded-[9px] border border-border"
                                                    />
                                                    Open result →
                                                </a>
                                            );
                                        return (
                                            <pre className="max-h-[140px] overflow-auto rounded-[9px] bg-secondary p-2 font-mono text-[10px] text-muted-foreground">
                                                {JSON.stringify(
                                                    testResult,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mb-[30px]">
                        <div className="ps-label mb-2">Flow JSON</div>
                        <div className="flex gap-3.5 text-[12px] font-semibold">
                            <button
                                type="button"
                                onClick={() => {
                                    const blob = new Blob(
                                        [JSON.stringify(steps, null, 2)],
                                        { type: 'application/json' },
                                    );
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'flow-definition.json';
                                    a.click();
                                    URL.revokeObjectURL(url);
                                }}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                            >
                                Export
                            </button>
                            <label className="cursor-pointer font-semibold text-brand-ink">
                                Import
                                <input
                                    type="file"
                                    accept="application/json,.json"
                                    className="hidden"
                                    onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!f) return;
                                        try {
                                            const parsed = JSON.parse(
                                                await f.text(),
                                            );
                                            if (Array.isArray(parsed))
                                                setSteps(parsed);
                                        } catch {
                                            /* ignore bad files */
                                        }
                                    }}
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {picker && (
                <AssetPicker
                    onClose={() => setPicker(null)}
                    onSelect={(url) => {
                        setArg(picker.step, picker.arg, url);
                        setPicker(null);
                    }}
                />
            )}
        </main>
    );
}
